import type { IPropertyBag } from '@deity-io/di-data-basics';
import { Icon, Modal } from '@deity-io/portal-ui';
import EntityPicker from 'components/FlowEditor/components/panels/FlowComponentSettings/EntityPicker';
import { EntityNameDescriptionFields } from 'components/FormFields';
import type { CreateEditModelInput } from 'contracts/interfaces';
import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect } from 'react';
import {
  GetPackageVersionContentInfoDocument,
  useCreateEndpointMutation,
  useUpdateEndpointMutation
} from 'services/graphql/generated/hooks';
import type { GetEndpointQuery } from 'services/graphql/generated/operations';
import { v4 } from 'uuid';
import * as Yup from 'yup';

type CreateEditEndpointInput = CreateEditModelInput & {
  webServiceUuid: string;
};

interface CreateEditEndpointModalProps {
  isOpen?: boolean;
  isEdit?: boolean;
  versionUuid: string;
  webServiceUuid?: string;
  editEndpoint?: GetEndpointQuery;
  setOpenState: (state: boolean) => void;
  onPackageCreated: () => void;
  refetchQueries?: Array<any>;
}

export const CreateEditEndpointModal: FC<CreateEditEndpointModalProps> = ({
  isOpen = false,
  setOpenState,
  onPackageCreated,
  isEdit,
  editEndpoint,
  versionUuid,
  webServiceUuid: webServiceUuidInput,
  refetchQueries
}) => {
  const options = {
    refetchQueries: [
      ...(refetchQueries || [
        {
          query: GetPackageVersionContentInfoDocument,
          variables: {
            versionUuid
          } as IPropertyBag
        }
      ])
    ]
  };

  const [create, { data: createData, loading: createLoading }] = useCreateEndpointMutation(options);
  const [update, { data: updateData, loading: updateLoading }] = useUpdateEndpointMutation(options);

  const CreateEndpointInputSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too short').max(100, 'Too long').required('This is a required field.'),
    description: Yup.string().min(2, 'Too short').max(255, 'Too long'),
    webServiceUuid: Yup.string().min(2, 'Too short').max(100, 'Too long').required('This is a required field.')
  });

  useEffect(() => {
    if (createData || updateData) {
      onPackageCreated();
      setOpenState(false);
    }
  }, [createData, updateData]);

  const createOrUpdate = (input: CreateEditEndpointInput) => {
    if (!input.description) {
      input.description = '';
    }

    return isEdit ? updateEndpoint(input) : createEndpoint(input);
  };

  const createEndpoint = (input: CreateEditEndpointInput) => {
    create({
      variables: {
        uuid: v4(),
        packageVersionUuid: versionUuid,
        webServiceUuid: input.webServiceUuid,
        name: input.name,
        description: input.description,
        schema: {
          type: 'rest',
          contentType: 'application/json',
          variables: {},
          protocol: 'https',
          method: 'GET'
        }
      }
    }).then();
  };

  const formik = useFormik({
    initialValues: {
      name: editEndpoint?.getEndpoint.name ?? '',
      description: editEndpoint?.getEndpoint.description ?? '',
      webServiceUuid: (webServiceUuidInput || editEndpoint?.getEndpoint.webServiceUuid) ?? ''
    },
    validateOnBlur: true,
    validationSchema: CreateEndpointInputSchema,
    onSubmit: values => createOrUpdate(values)
  });

  useEffect(() => {
    if (editEndpoint?.getEndpoint) {
      const { name, description, webServiceUuid } = editEndpoint.getEndpoint;
      formik.setValues({ name: name!, description: description!, webServiceUuid: webServiceUuid! });
    }
  }, [editEndpoint?.getEndpoint]);

  const updateEndpoint = (input: CreateEditEndpointInput) => {
    update({
      variables: {
        packageVersionUuid: versionUuid,
        uuid: editEndpoint?.getEndpoint.uuid ?? '',
        name: input.name,
        description: input.description
      }
    }).then();
  };

  const confirmText = () => {
    if (!isEdit) {
      return `${createLoading ? 'Creating' : 'Create'} Endpoint`;
    }

    return `${updateLoading ? 'Updating' : 'Update'} Endpoint`;
  };

  if (isEdit && !editEndpoint) {
    return null!;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setOpenState(false)}
      label={`${isEdit ? 'Edit' : 'New'} Endpoint`}
      title="Enter a name and description."
      closeIcon={<Icon icon="close" />}
      confirmText={confirmText()}
      onConfirm={() => formik.handleSubmit()}
    >
      <form onSubmit={formik.handleSubmit}>
        <EntityNameDescriptionFields
          errors={formik.errors}
          values={formik.values}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={formik.setFieldTouched}
          entity="Endpoint"
        />
        <EntityPicker
          isEditable={!webServiceUuidInput}
          property="webServiceUuid"
          description="Set the source service"
          entity="webService"
          entityUUID={webServiceUuidInput || formik.values.webServiceUuid}
          onChange={(key: string, entityId: string): void => {
            formik.setFieldValue(key, entityId);
          }}
        />
      </form>
    </Modal>
  );
};
